import React, { useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Checkbox } from "primereact/checkbox";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import { finalizeDocument } from "../../../../../services/clearing/bills";
import { isProduction } from "../../../../../utils/environment";

export type ValidationInfo = {
  type: "warning" | "info" | "error" | "WARNING" | "INFO" | "ERROR";
  documentId: number;
  customerId: number;
  title: string | null;
  date: string | null;
  serviceDate: string | null;
  currentVatOption: string | null;
  previousDocumentId: number | null;
  previousVatOption: string | null;
  isDraft: boolean;
  message: string;
};

export function easybillDocumentUrl(documentId: number): string {
  return `https://${isProduction ? "c625304" : "c624230"}.easybill.de/docs?document=${documentId}`;
}

export function FinalizeDialog({
  visible,
  warnings,
  onClose,
  header,
  buttonLabel,
  successMessage,
  delaySeconds,
}: {
  visible: boolean;
  warnings: ValidationInfo[];
  onClose: () => void;
  header: string;
  buttonLabel: string;
  successMessage: string;
  delaySeconds: number;
}) {
  const localToast = useRef(null);
  const [ignoredWarnings, setIgnoredWarnings] = useState<Set<number>>(new Set());
  const [showFinalized, setShowFinalized] = useState(true);
  const [showValidated, setShowValidated] = useState(true);
  const [finalizeLoading, setFinalizeLoading] = useState(false);
  const [finalizingDocId, setFinalizingDocId] = useState<number | null>(null);
  const [finalizedDocs, setFinalizedDocs] = useState<Set<number>>(new Set());
  const [countdown, setCountdown] = useState<number | null>(null);
  const abortRef = useRef(false);

  const toggleIgnore = (documentId: number) => {
    setIgnoredWarnings((prev) => {
      const next = new Set(prev);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  };

  const canFinalize = (row: ValidationInfo) => {
    if (row.type?.toLowerCase() === "error") return false;
    if (row.isDraft === false) return false;
    if (finalizedDocs.has(row.documentId)) return false;
    if (row.type?.toLowerCase() === "info") return true;
    if (ignoredWarnings.has(row.documentId)) return true;
    return false;
  };

  const delayWithCountdown = (seconds: number) => {
    return new Promise<void>((resolve) => {
      setCountdown(seconds);
      let remaining = seconds;
      const interval = setInterval(() => {
        remaining -= 1;
        setCountdown(remaining);
        if (remaining <= 0 || abortRef.current) {
          clearInterval(interval);
          setCountdown(null);
          resolve();
        }
      }, 1000);
    });
  };

  const finalizeWithRetry = async (documentId: number, maxRetries = 3): Promise<boolean> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      if (abortRef.current) return false;
      try {
        await finalizeDocument(documentId);
        return true;
      } catch (e: any) {
        if (abortRef.current) return false;
        const status = e?.response?.status;
        const isTimeout = status === 504 || status === 408 || e?.code === "ECONNABORTED";
        if (isTimeout && attempt < maxRetries) {
          localToast.current?.show({
            severity: "warn",
            summary: "Timeout",
            detail: `Document ${documentId} timed out, retrying (${attempt}/${maxRetries})...`,
          });
          continue;
        }
        throw e;
      }
    }
    return false;
  };

  const handleFinalize = async () => {
    setFinalizeLoading(true);
    abortRef.current = false;
    let completedAll = true;

    const docsToFinalize = Array.from(
      new Map(
        warnings
          .filter(canFinalize)
          .map((row) => [row.documentId, row])
      ).values()
    );

    for (let i = 0; i < docsToFinalize.length; i++) {
      if (abortRef.current) break;

      const doc = docsToFinalize[i];
      setFinalizingDocId(doc.documentId);

      try {
        const finalized = await finalizeWithRetry(doc.documentId);
        if (finalized) {
          setFinalizedDocs((prev) => new Set(prev).add(doc.documentId));
        }
        if (abortRef.current) break;
      } catch (e: any) {
        completedAll = false;
        if (abortRef.current) break;
        const detail = e?.response?.data?.message || e?.response?.data || e?.message || `Failed to finalize document ${doc.documentId}.`;
        localToast.current?.show({ severity: "error", summary: "Error", detail });
        break;
      }

      if (i < docsToFinalize.length - 1 && !abortRef.current) {
        await delayWithCountdown(delaySeconds);
      }
    }

    setFinalizingDocId(null);

    if (!abortRef.current && completedAll) {
      localToast.current?.show({
        severity: "success",
        summary: "Success",
        detail: successMessage,
      });
    }

    setFinalizeLoading(false);
  };

  const handleAbort = () => {
    abortRef.current = true;
  };

  const ignoreBodyTemplate = (rowData) => {
    if (rowData.isDraft === false || finalizedDocs.has(rowData.documentId)) {
      return <span className="font-bold" style={{ color: "#fff", border: "2px solid #fff", padding: "0.2rem 0.5rem", borderRadius: "4px" }}>FINAL</span>;
    }
    if (rowData.type?.toLowerCase() === "error") {
      return <span className="font-bold" style={{ color: "#fff", border: "2px solid #fff", padding: "0.2rem 0.5rem", borderRadius: "4px" }}>DELETE</span>;
    }
    if (finalizingDocId === rowData.documentId) {
      return <span className="flex align-items-center gap-2"><i className="pi pi-spin pi-spinner" style={{ fontSize: "1.2rem" }} /> Finalizing document</span>;
    }
    if (rowData.type?.toLowerCase() === "info") {
      return <span className="font-bold" style={{ color: "#22C55E", border: "2px solid #22C55E", padding: "0.2rem 0.5rem", borderRadius: "4px" }}>VALIDATED</span>;
    }
    return (
      <Checkbox
        checked={ignoredWarnings.has(rowData.documentId)}
        onChange={() => toggleIgnore(rowData.documentId)}
        disabled={finalizeLoading}
      />
    );
  };

  const rowClassName = (row) => {
    if (row.isDraft === false || finalizedDocs.has(row.documentId)) return "row-finalized";
    if (row.type?.toLowerCase() === "error") return "row-error";
    if (finalizingDocId === row.documentId) return "row-finalizing";
    if (row.type?.toLowerCase() === "info") return "row-validated";
    if (ignoredWarnings.has(row.documentId)) return "row-ignored";
    return "row-warning";
  };

  return (
    <>
      <style>{`
        .row-finalized td { background: #166534 !important; color: #fff !important; }
        .row-error td { background: #7f1d1d !important; color: #fff !important; }
        .row-finalizing td { background: #dbeafe !important; }
        .row-validated td { background: #f0fdf4 !important; }
        .row-ignored td { background: #fefce8 !important; }
        .row-warning td { background: #fef2f2 !important; }
      `}</style>
      <Dialog
        header={header}
        visible={visible}
        style={{ width: "90vw", height: "90vh", fontSize: "0.85rem" }}
        contentStyle={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}
        closable={!finalizeLoading}
        onHide={onClose}
      >
        <Toast ref={localToast} />

        {warnings.length > 0 ? (
          <>
            <p className="mb-3">
              Please review the VAT validation results. Ignore each warning to proceed with finalization.
            </p>
            <div className="flex gap-3 mb-3" style={{ fontSize: "0.85rem" }}>
              <span style={{ color: "#166534", fontWeight: "bold" }}>
                {warnings.filter((w) => w.isDraft === false || finalizedDocs.has(w.documentId)).length} Final
              </span>
              <span style={{ color: "#16a34a", fontWeight: "bold" }}>
                {warnings.filter((w) => w.type?.toLowerCase() === "info" && w.isDraft !== false && !finalizedDocs.has(w.documentId)).length} Validated
              </span>
              <span style={{ color: "#ca8a04", fontWeight: "bold" }}>
                {warnings.filter((w) => w.type?.toLowerCase() === "warning" && w.isDraft !== false && !finalizedDocs.has(w.documentId)).length} Warnings
              </span>
              <span style={{ color: "#991b1b", fontWeight: "bold" }}>
                {warnings.filter((w) => w.type?.toLowerCase() === "error").length} Errors
              </span>
            </div>
            <div className="flex flex-column gap-2 mb-3">
              <div className="flex align-items-center gap-2">
                <Checkbox
                  inputId="showFinalized"
                  checked={showFinalized}
                  onChange={(e) => setShowFinalized(e.checked)}
                  disabled={finalizeLoading}
                />
                <label htmlFor="showFinalized">Show finalized documents</label>
              </div>
              <div className="flex align-items-center gap-2">
                <Checkbox
                  inputId="showValidated"
                  checked={showValidated}
                  onChange={(e) => setShowValidated(e.checked)}
                  disabled={finalizeLoading}
                />
                <label htmlFor="showValidated">Show validated documents</label>
              </div>
            </div>
            <div style={{ flex: 1, overflow: "hidden" }}>
              <DataTable value={warnings.filter((w) => {
                const isFinalized = w.isDraft === false || finalizedDocs.has(w.documentId);
                if (isFinalized && !showFinalized) return false;
                const isInfo = w.type?.toLowerCase() === "info";
                if (isInfo && !isFinalized && !showValidated) return false;
                return true;
              })} size="small" scrollable scrollHeight="flex"
                rowClassName={rowClassName}
                style={{ fontSize: "0.75rem" }}
              >
                <Column header="Status" body={ignoreBodyTemplate} style={{ width: "10rem", textAlign: "center" }} />
                <Column field="documentId" header="Document ID" sortable body={(row) => (
                  <a
                    href={easybillDocumentUrl(row.documentId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="no-underline"
                    style={{ color: "#2563eb" }}
                  >
                    {row.documentId}
                  </a>
                )} />
                <Column field="customerId" header="Customer ID" sortable />
                <Column field="title" header="Title" sortable />
                <Column field="date" header="Date" sortable />
                <Column field="serviceDate" header="Service Date" sortable />
                <Column header="VAT previous -> current" sortable sortField="currentVatOption" body={(row) => `${row.previousVatOption ?? "Default"} -> ${row.currentVatOption ?? "Default"}`} />
                <Column field="message" header="Message" />
              </DataTable>
            </div>
          </>
        ) : (
          <p>No VAT issues found. You can proceed to finalize.</p>
        )}

        <div className="flex justify-content-end align-items-center mt-3 gap-3">
          {countdown !== null && (
            <span className="text-color-secondary font-semibold">Next document in {countdown}s...</span>
          )}
          {finalizeLoading ? (
            <Button
              label="Stop"
              icon="pi pi-times"
              severity="danger"
              onClick={handleAbort}
            />
          ) : (
            <Button
              label={buttonLabel}
              icon="pi pi-send"
              onClick={handleFinalize}
              disabled={!warnings.some(canFinalize)}
            />
          )}
        </div>
      </Dialog>
    </>
  );
}
